import { useMutation, useQuery } from '@tanstack/react-query'
import type { PostLongWritesActivityResultProps } from '@/data/long-writes'
import {
  putLongWritesActivityResult,
  deleteActivityResult,
  getStudentResults,
  getTopicIdeasList,
  postLongWritesActivityResult,
} from '@/data/long-writes'
import cuid from 'cuid'
import { DEFAULT_CONTENT_GRADE } from '@wl/activity'

interface UseLongWritesOptions {
  studentId: string
  contentGrade?: string | undefined
}
export interface ResultToDeleteProps {
  activityId: string
  activityResultId: string
}

export const WRITE_YOUR_OWN_ACTIVITY_ID = 'c000longwritewriteyourown'

const queryKeys = {
  inProgress(studentId: string) {
    return [{ scope: 'long-writes-in-progress', studentId }] as const
  },
  topicsIdeasList(studentId: string, contentGrade?: string) {
    return [
      { scope: 'long-writes-topics-ideas-list', studentId, contentGrade },
    ] as const
  },
}

export function useLongWrites({
  studentId,
  contentGrade,
}: UseLongWritesOptions) {
  const inProgressResults = useQuery({
    queryKey: queryKeys.inProgress(studentId),
    queryFn: async ({ signal = null }) => {
      const result = await getStudentResults(
        {
          studentId,
        },
        {
          signal,
        },
      )
      return result ?? []
    },
  })

  const deleteResult = useMutation({
    mutationFn: async (resultToDelete: ResultToDeleteProps) => {
      await deleteActivityResult({
        ...resultToDelete,
        longWriteResultDelete: { eventId: cuid() },
      })
    },
    onSuccess: async () => {
      await inProgressResults.refetch()
    },
  })

  const postActivityResult = useMutation({
    mutationFn: async (props: PostLongWritesActivityResultProps) => {
      await postLongWritesActivityResult(props)
    },
    onSuccess: async () => {
      await inProgressResults.refetch()
    },
  })

  const updateActivityResult = useMutation({
    mutationFn: async (props: PostLongWritesActivityResultProps) => {
      await putLongWritesActivityResult(props)
    },
  })

  const topicIdeasList = useQuery({
    queryKey: queryKeys.topicsIdeasList(studentId, contentGrade),
    queryFn: async ({ signal = null }) => {
      const result = await getTopicIdeasList(
        {
          gradePosition: Number(contentGrade ?? DEFAULT_CONTENT_GRADE),
        },
        {
          signal,
        },
      )
      return result ?? []
    },
  })

  return {
    inProgressResults,
    topicIdeasList,
    postActivityResult: postActivityResult.mutateAsync,
    updateActivityResult: updateActivityResult.mutateAsync,
    deleteResult: deleteResult.mutateAsync,
  }
}

export function parseStepId(stepId: string) {
  const stepParts = stepId.split('-').map((part) => Number.parseInt(part, 10))
  return { mainStep: stepParts[0] ?? 0, subStep: stepParts[1] ?? null }
}
